import {
  HttpError,
  IResourceComponentsProps,
  useApiUrl,
  useNavigation,
  useNotification,
  usePermissions,
} from "@pankod/refine-core";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Icons,
  Input,
  List,
  Modal,
  Row,
  Select,
  ShowButton,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  useTable,
} from "@pankod/refine-antd";
import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useState } from "react";

import { ILegacyConsumerLog, ILegacySendSummary } from "interfaces";
import {
  ITEM_TARGET_OPTIONS,
  RUN_OUTCOME_OPTIONS,
  SEND_OUTCOME_OPTIONS,
  UserRole,
  runOutcomeStatus,
  sendOutcomeStatus,
} from "enums";
import { dateFormater } from "utils/date-formater";
import { axiosInstance } from "services/axios";
// Mapa de rótulo do status já existe na tela de Orçamentos — reusar evita
// sinonimizar "Status SIGO" entre telas (regra de vocabulário do design system).
import { getBudgetStatusLabel } from "pages/budgets/list.helpers";
import {
  acceptedRatio,
  formatDurationSeconds,
  hasFailures,
  metricNumber,
  metricPercent,
  summaryParamsFromFilters,
} from "./aggregations";

const { Text } = Typography;

const KIND_SEND = "beneficiary_send";
const KIND_RUN = "reconciliation_run";

/** Enquanto um reprocessamento recém-disparado não aparece, a tela se atualiza sozinha por 60s. */
const POLL_INTERVAL_MS = 5000;
const POLL_WINDOW_MS = 60000;

/** Navy institucional do card de stat principal — mesmo valor usado pelo `DashboardV2`. */
const BRAND_NAVY = "#003D85";
const BRAND_ON_NAVY = "#FFFFFF";
const BRAND_ON_NAVY_MUTED = "rgba(255,255,255,.7)";

interface SendSearchValues {
  control_number?: string;
  outcome?: string;
  item_target?: string;
  period?: [Dayjs, Dayjs];
}

interface RunSearchValues {
  control_number?: string;
  outcome?: string;
  period?: [Dayjs, Dayjs];
}

const periodFilters = (period?: [Dayjs, Dayjs]) => [
  {
    field: "start_date",
    operator: "eq" as const,
    value: period?.[0] ? dayjs(period[0]).format("YYYY-MM-DD") : undefined,
  },
  {
    field: "end_date",
    operator: "eq" as const,
    value: period?.[1] ? dayjs(period[1]).format("YYYY-MM-DD") : undefined,
  },
];

/**
 * Mensagem de falha do reprocessamento. A etapa vem do backend (padrão da spec 028) e é o que
 * distingue "não achei o orçamento" de "não consegui publicar".
 */
export const resolveDispatchMessage = (error: any): string => {
  if (error?.response?.status === 404) {
    return "Nº de controle não encontrado.";
  }
  const stage = error?.response?.data?.stage;
  if (stage) {
    return `Falha no reprocessamento — etapa: ${stage}`;
  }
  return "Não foi possível reprocessar o orçamento. Tente novamente.";
};

/**
 * Card de resumo. Sem valor, exibe "—" — ausência não é zero (lição da spec 033).
 *
 * O card `brand` é superfície de marca: navy nos **dois** temas, como o `DashboardV2` já faz (é de
 * lá que vem o navy inline). O acento de falha usa `Text type="danger"` em vez de hex, porque a
 * semântica do antd é a única que acompanha o dark mode sem depender de variável declarada — a
 * armadilha que a spec 036 documentou (var inexistente invalida a cor em silêncio).
 */
export const SummaryCard: React.FC<{
  title: string;
  value: string;
  caption: string;
  brand?: boolean;
  danger?: boolean;
}> = ({ title, value, caption, brand, danger }) => {
  const valueColor = brand ? BRAND_ON_NAVY : undefined;
  const valueType = !brand && danger ? "danger" : undefined;

  return (
  <Card
    bodyStyle={{ padding: 20 }}
    style={brand ? { background: BRAND_NAVY, borderColor: BRAND_NAVY } : undefined}
  >
    <Text
      type={valueType}
      style={{ display: "block", fontSize: 24, fontWeight: 700, color: valueColor }}
    >
      {value}
    </Text>
    <Text strong style={{ display: "block", color: brand ? BRAND_ON_NAVY : undefined }}>
      {title}
    </Text>
    <Text
      type={brand ? undefined : "secondary"}
      style={{ fontSize: 12, color: brand ? BRAND_ON_NAVY_MUTED : undefined }}
    >
      {caption}
    </Text>
  </Card>
  );
};

export const LegacySendsList: React.FC<IResourceComponentsProps> = () => {
  const permissions = usePermissions();
  const isAdmin = permissions.data === UserRole.Admin;

  const apiUrl = useApiUrl();
  const { show } = useNavigation();
  const toast = useNotification();

  const [summary, setSummary] = useState<ILegacySendSummary | null>(null);
  const [summaryFailed, setSummaryFailed] = useState(false);
  const [controlNumber, setControlNumber] = useState("");
  const [reprocessing, setReprocessing] = useState(false);
  const [dispatchError, setDispatchError] = useState<string | null>(null);
  const [pollingUntil, setPollingUntil] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState(KIND_SEND);

  const sends = useTable<ILegacyConsumerLog, HttpError, SendSearchValues>({
    permanentFilter: [{ field: "kind", operator: "eq", value: KIND_SEND }],
    onSearch: (values) => [
      { field: "control_number", operator: "eq", value: values.control_number },
      { field: "outcome", operator: "eq", value: values.outcome },
      { field: "item_target", operator: "eq", value: values.item_target },
      ...periodFilters(values.period),
    ],
    initialCurrent: 1,
    initialPageSize: 10,
    hasPagination: true,
  });

  const runs = useTable<ILegacyConsumerLog, HttpError, RunSearchValues>({
    permanentFilter: [{ field: "kind", operator: "eq", value: KIND_RUN }],
    onSearch: (values) => [
      { field: "control_number", operator: "eq", value: values.control_number },
      { field: "outcome", operator: "eq", value: values.outcome },
      ...periodFilters(values.period),
    ],
    initialCurrent: 1,
    initialPageSize: 10,
    hasPagination: true,
  });

  // Filtros da aba visível: o resumo tem de falar do MESMO conjunto que a tabela mostra, senão a
  // caption "no período filtrado" mente (R01 do review).
  const activeFilters = activeTab === KIND_SEND ? sends.filters : runs.filters;
  // Serializado para servir de dependência estável do efeito — o array de filtros é recriado a cada
  // render e dispararia a chamada em loop.
  const summaryParamsKey = JSON.stringify(
    summaryParamsFromFilters(activeFilters as any[])
  );

  const loadSummary = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${apiUrl}/v1/legacy-consumer-log/summary`,
        { params: JSON.parse(summaryParamsKey) }
      );
      setSummary(response.data ?? null);
      setSummaryFailed(false);
    } catch (error) {
      // Falha do resumo não derruba a tela: os cards mostram "—" e a tabela segue funcionando.
      setSummary(null);
      setSummaryFailed(true);
    }
  }, [apiUrl, summaryParamsKey]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const refreshAll = useCallback(() => {
    if (activeTab === KIND_SEND) {
      sends.tableQueryResult.refetch();
    } else {
      runs.tableQueryResult.refetch();
    }
    loadSummary();
  }, [activeTab, loadSummary, runs.tableQueryResult, sends.tableQueryResult]);

  // Polling disciplinado (RN11): só depois de um reprocessamento, por uma janela curta, e só na
  // aba visível. Não há estado "em andamento" persistido para observar indefinidamente.
  useEffect(() => {
    if (!pollingUntil) {
      return undefined;
    }
    const timer = setInterval(() => {
      if (Date.now() > pollingUntil) {
        setPollingUntil(null);
        return;
      }
      refreshAll();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [pollingUntil, refreshAll]);

  async function reprocess(numberToSend: string) {
    setReprocessing(true);
    setDispatchError(null);
    try {
      const response = await axiosInstance.post(
        `${apiUrl}/v1/legacy-consumer-log/reprocess`,
        { control_number: numberToSend }
      );
      toast.open?.({
        message: "Sucesso",
        description: "Orçamento enviado para reprocessamento.",
        type: "success",
      });
      setPollingUntil(Date.now() + POLL_WINDOW_MS);
      return response.data;
    } catch (error: any) {
      setDispatchError(resolveDispatchMessage(error));
      return undefined;
    } finally {
      setReprocessing(false);
    }
  }

  function confirmReprocess() {
    const numberToSend = controlNumber.trim();
    if (!numberToSend) {
      setDispatchError("Este campo é obrigatório.");
      return;
    }
    Modal.confirm({
      title: "Reprocessar orçamento por completo?",
      icon: <Icons.ExclamationCircleOutlined />,
      content: `O orçamento ${numberToSend} entrará na fila de reconciliação. O sistema vai consultar o status no SIGO e atualizar o orçamento e a indicação. Os dados do beneficiário não são reenviados para o sistema legado.`,
      okText: "Reprocessar",
      cancelText: "Cancelar",
      onOk: () => reprocess(numberToSend),
    });
  }

  // Textos completos, não montados por concatenação de gênero: mais legível e não deixa o
  // resultado depender de encaixe de sufixo (R05 do review).
  const emptyText = (hasFilter: boolean, entity: "envio" | "execução") => {
    const messages = {
      envio: {
        filtered: "Nenhum envio encontrado para os filtros aplicados.",
        empty: "Nenhum envio registrado no período.",
      },
      execução: {
        filtered: "Nenhuma execução encontrada para os filtros aplicados.",
        empty: "Nenhuma execução registrada no período.",
      },
    };
    return hasFilter ? messages[entity].filtered : messages[entity].empty;
  };

  const hasAppliedFilter = (filters: any[]) =>
    (filters ?? []).some(
      (filter) =>
        "value" in filter && Boolean(filter.value) && filter.field !== "kind"
    );

  return (
    <List
      title="Envios ao Legado"
      headerButtons={
        <Button icon={<Icons.ReloadOutlined />} onClick={refreshAll}>
          Atualizar
        </Button>
      }
    >
      {summaryFailed && (
        <Card style={{ marginBottom: 16 }}>
          <Text type="danger">
            Não foi possível carregar os indicadores. Os registros abaixo seguem
            disponíveis.
          </Text>
        </Card>
      )}

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane tab="Envios ao Legado" key={KIND_SEND}>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} lg={6}>
              <SummaryCard
                brand
                title="Envios no Período"
                value={metricNumber(summary?.total_sends)}
                caption="Orçamentos enviados ao legado no período filtrado"
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <SummaryCard
                title="Com Falha"
                value={metricNumber(summary?.failed_sends)}
                caption="Envios com pelo menos um item recusado ou com erro"
                danger={hasFailures(summary?.failed_sends)}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <SummaryCard
                title="Taxa de Sucesso"
                value={metricPercent(summary?.success_rate)}
                caption="Envios com todos os itens aceitos pelo legado"
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <SummaryCard
                title="Último Envio"
                value={dateFormater(summary?.last_send_at ?? undefined) ?? "—"}
                caption="Data e hora do envio mais recente"
              />
            </Col>
          </Row>

          <Card title="Filtrar" style={{ marginBottom: 16 }}>
            <Form {...sends.searchFormProps} layout="vertical">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={6}>
                  <Form.Item
                    name="control_number"
                    label="Nº de Controle"
                    style={{ marginBottom: 0 }}
                  >
                    <Input placeholder="Digite o nº de controle" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Form.Item name="period" label="Período" style={{ marginBottom: 0 }}>
                    <DatePicker.RangePicker
                      format="DD/MM/YYYY"
                      placeholder={["Data inicial", "Data final"]}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Form.Item name="outcome" label="Situação" style={{ marginBottom: 0 }}>
                    <Select
                      options={SEND_OUTCOME_OPTIONS}
                      placeholder="Selecione a situação"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Form.Item
                    name="item_target"
                    label="Tipo de Item"
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      options={ITEM_TARGET_OPTIONS}
                      placeholder="Selecione o tipo de item"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row justify="end" style={{ marginTop: 16 }}>
                <Space>
                  <Button
                    icon={<Icons.ClearOutlined />}
                    onClick={() => {
                      sends.searchFormProps.form?.resetFields();
                      sends.searchFormProps.form?.submit();
                    }}
                  >
                    Limpar
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<Icons.SearchOutlined />}
                    onClick={() => sends.searchFormProps.form?.submit()}
                  >
                    Buscar
                  </Button>
                </Space>
              </Row>
            </Form>
          </Card>

          <Table
            {...sends.tableProps}
            rowKey="_id"
            scroll={{ x: 960 }}
            locale={{
              emptyText: emptyText(hasAppliedFilter(sends.filters as any[]), "envio"),
            }}
            pagination={{
              ...sends.tableProps.pagination,
              showTotal: (total: number, range: [number, number]) =>
                `${range[0]}-${range[1]} de ${total} registros`,
            }}
          >
            <Table.Column
              dataIndex="control_number"
              title="Nº de Controle"
              render={(value) => (
                <span style={{ whiteSpace: "nowrap" }}>{value || "—"}</span>
              )}
            />
            <Table.Column
              dataIndex="started_at"
              title="Data/Hora"
              render={(value) => (
                <span style={{ whiteSpace: "nowrap" }}>
                  {dateFormater(value) ?? "—"}
                </span>
              )}
            />
            <Table.Column
              dataIndex="outcome"
              title="Situação"
              render={(value) => {
                const status = sendOutcomeStatus(value);
                return <Tag color={status.color}>{status.label}</Tag>;
              }}
            />
            <Table.Column
              dataIndex="counters"
              title="Itens Aceitos"
              align="right"
              render={(_value, record: ILegacyConsumerLog) => acceptedRatio(record)}
            />
            <Table.Column
              dataIndex="dependents_count"
              title="Dependentes"
              align="right"
              render={(value) => metricNumber(value)}
            />
            <Table.Column
              dataIndex="duration_seconds"
              title="Duração"
              align="right"
              render={(value) => formatDurationSeconds(value)}
            />
            <Table.Column<ILegacyConsumerLog>
              title="Ações"
              dataIndex="actions"
              render={(_value, record) => (
                <ShowButton
                  size="small"
                  recordItemId={record._id}
                  onClick={() => show("v1/legacy-consumer-log", record._id)}
                />
              )}
            />
          </Table>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Reconciliação" key={KIND_RUN}>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} lg={6}>
              <SummaryCard
                brand
                title="Última Execução"
                value={dateFormater(summary?.last_run_at ?? undefined) ?? "—"}
                caption="Início da execução mais recente do cron"
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <SummaryCard
                title="Orçamentos Varridos"
                value={metricNumber(summary?.last_run_budget_count)}
                caption="Orçamentos elegíveis na última execução"
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <SummaryCard
                title="Processados"
                value={metricNumber(summary?.last_run_processed_count)}
                caption="Orçamentos reconciliados com o SIGO"
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <SummaryCard
                title="Com Erro"
                value={metricNumber(summary?.last_run_error_count)}
                caption="Orçamentos que falharam na reconciliação"
                danger={hasFailures(summary?.last_run_error_count)}
              />
            </Col>
          </Row>

          {/* Reprocessamento por nº de controle: alcança o orçamento que nunca teve envio
              registrado — justamente o caso que a lista de envios não mostra. Só Admin (RN10). */}
          {isAdmin && (
            <Card title="Reprocessar Orçamento" style={{ marginBottom: 16 }}>
              {dispatchError && (
                <Text type="danger" style={{ display: "block", marginBottom: 16 }}>
                  {dispatchError}
                </Text>
              )}
              <Row gutter={[16, 16]} align="bottom">
                <Col xs={24} sm={12} lg={8}>
                  <Form layout="vertical">
                    <Form.Item label="Nº de Controle" style={{ marginBottom: 0 }}>
                      <Input
                        value={controlNumber}
                        onChange={(event) => setControlNumber(event.target.value)}
                        placeholder="Digite o nº de controle"
                      />
                    </Form.Item>
                  </Form>
                </Col>
                <Col xs={24} sm={12} lg={16}>
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Text type="secondary">
                      Reconcilia status e indicação com o SIGO, como o cron faz
                      automaticamente. Não reenvia os dados do beneficiário ao sistema
                      legado.
                    </Text>
                    <Button
                      type="primary"
                      loading={reprocessing}
                      icon={<Icons.ReloadOutlined />}
                      onClick={confirmReprocess}
                    >
                      Reprocessar Completo
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Card>
          )}

          <Card title="Filtrar" style={{ marginBottom: 16 }}>
            <Form {...runs.searchFormProps} layout="vertical">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    name="control_number"
                    label="Nº de Controle"
                    style={{ marginBottom: 0 }}
                  >
                    <Input placeholder="Digite o nº de controle" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item name="period" label="Período" style={{ marginBottom: 0 }}>
                    <DatePicker.RangePicker
                      format="DD/MM/YYYY"
                      placeholder={["Data inicial", "Data final"]}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item name="outcome" label="Situação" style={{ marginBottom: 0 }}>
                    <Select
                      options={RUN_OUTCOME_OPTIONS}
                      placeholder="Selecione a situação"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row justify="end" style={{ marginTop: 16 }}>
                <Space>
                  <Button
                    icon={<Icons.ClearOutlined />}
                    onClick={() => {
                      runs.searchFormProps.form?.resetFields();
                      runs.searchFormProps.form?.submit();
                    }}
                  >
                    Limpar
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<Icons.SearchOutlined />}
                    onClick={() => runs.searchFormProps.form?.submit()}
                  >
                    Buscar
                  </Button>
                </Space>
              </Row>
            </Form>
          </Card>

          <Table
            {...runs.tableProps}
            rowKey="_id"
            scroll={{ x: 960 }}
            locale={{
              emptyText: emptyText(hasAppliedFilter(runs.filters as any[]), "execução"),
            }}
            pagination={{
              ...runs.tableProps.pagination,
              showTotal: (total: number, range: [number, number]) =>
                `${range[0]}-${range[1]} de ${total} registros`,
            }}
            expandable={{
              // Só linhas com falha expandem: é o que responde "quais orçamentos falharam".
              rowExpandable: (record: ILegacyConsumerLog) =>
                Boolean(record.failed_budgets && record.failed_budgets.length > 0),
              expandedRowRender: (record: ILegacyConsumerLog) => (
                <FailedBudgetsTable record={record} />
              ),
            }}
          >
            <Table.Column
              dataIndex="started_at"
              title="Início"
              render={(value) => (
                <span style={{ whiteSpace: "nowrap" }}>
                  {dateFormater(value) ?? "—"}
                </span>
              )}
            />
            <Table.Column
              dataIndex="finished_at"
              title="Fim"
              render={(value) => (
                <span style={{ whiteSpace: "nowrap" }}>
                  {dateFormater(value) ?? "—"}
                </span>
              )}
            />
            <Table.Column
              dataIndex="duration_seconds"
              title="Duração"
              align="right"
              render={(value) => formatDurationSeconds(value)}
            />
            <Table.Column
              dataIndex="budget_count"
              title="Varridos"
              align="right"
              render={(value) => metricNumber(value)}
            />
            <Table.Column
              dataIndex="processed_count"
              title="Processados"
              align="right"
              render={(value) => metricNumber(value)}
            />
            <Table.Column
              dataIndex="error_count"
              title="Com Erro"
              align="right"
              render={(value) => metricNumber(value)}
            />
            <Table.Column
              dataIndex="outcome"
              title="Situação"
              render={(value) => {
                const status = runOutcomeStatus(value);
                return <Tag color={status.color}>{status.label}</Tag>;
              }}
            />
            <Table.Column
              dataIndex="days_back_check_update"
              title="Dias Retroativos"
              align="right"
              render={(value) => metricNumber(value)}
            />
          </Table>
        </Tabs.TabPane>
      </Tabs>
    </List>
  );
};

/** Orçamentos que falharam numa execução — o "quais" que os contadores não respondem. */
export const FailedBudgetsTable: React.FC<{ record: ILegacyConsumerLog }> = ({
  record,
}) => {
  const rows = (record.failed_budgets ?? []).map((entry, index) => ({
    key: `${entry.control_number ?? index}`,
    ...entry,
  }));

  return (
    <>
      {record.failed_budgets_truncated && (
        <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
          Lista limitada aos primeiros registros — houve mais falhas nesta execução.
        </Text>
      )}
      <Table dataSource={rows} pagination={false} size="small" rowKey="key">
        <Table.Column
          dataIndex="control_number"
          title="Nº de Controle"
          render={(value) => value || "—"}
        />
        <Table.Column
          dataIndex="status_sigo"
          title="Status SIGO"
          render={(value) =>
            typeof value === "number" ? getBudgetStatusLabel(value, "—") : "—"
          }
        />
        <Table.Column
          dataIndex="error_type"
          title="Motivo"
          render={(value) => value || "—"}
        />
      </Table>
    </>
  );
};
